import { repApi } from './repApi';
import type {
  AgreementLetter,
  AgreementStatus,
  Contribution,
  ContributionSummary,
  HistoryEntry,
  Redistribution,
} from '@/types';

export interface RedistributionBody extends Partial<Redistribution> {
  PM_contributions?: Contribution[];
  Personnel_contributions?: Contribution[];
  PRD_contributions?: Contribution[];
  SM_contributions?: Contribution[];
  '500mr_contributions'?: Contribution[];
  '5r_contributions'?: Contribution[];
}

export const redistributionApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    listRedistribution: builder.query<Redistribution[], void>({
      query: () => '/redistribution',
      providesTags: ['Redistribution'],
    }),
    getRedistribution: builder.query<Redistribution, number>({
      query: (id) => `/redistribution/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Redistribution', id }],
    }),
    redistributionHistory: builder.query<HistoryEntry[], number>({
      query: (id) => `/redistribution/${id}/history`,
      keepUnusedDataFor: 0,
    }),
    redistributionContributions: builder.query<ContributionSummary[], number>({
      query: (id) => `/redistribution/${id}/contributions`,
      providesTags: (_r, _e, id) => [{ type: 'Redistribution', id }],
    }),
    createRedistribution: builder.mutation<Redistribution, RedistributionBody>({
      query: (body) => ({ url: '/redistribution', method: 'POST', body }),
      invalidatesTags: ['Redistribution', 'Agreement'],
    }),
    updateRedistribution: builder.mutation<
      Redistribution,
      { id: number; body: RedistributionBody }
    >({
      query: ({ id, body }) => ({ url: `/redistribution/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        'Redistribution',
        { type: 'Redistribution', id },
        'Agreement',
      ],
    }),
    deleteRedistribution: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/redistribution/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Redistribution', 'Agreement'],
    }),
    // Agreement letters
    getAgreement: builder.query<AgreementLetter, number>({
      query: (contributionId) => `/redistribution/agreements/${contributionId}`,
      providesTags: (_r, _e, id) => [{ type: 'Agreement', id }],
    }),
    updateAgreement: builder.mutation<
      AgreementLetter,
      { contributionId: number; pvStatus?: AgreementStatus; agreementText?: string }
    >({
      query: ({ contributionId, ...body }) => ({
        url: `/redistribution/agreements/${contributionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { contributionId }) => [
        'Agreement',
        { type: 'Agreement', id: contributionId },
      ],
    }),
    resetAgreements: builder.mutation<{ reset: number }, { agreementText: string }>({
      query: (body) => ({ url: '/redistribution/agreements/reset', method: 'POST', body }),
      invalidatesTags: ['Agreement'],
    }),
  }),
});

export const {
  useListRedistributionQuery,
  useGetRedistributionQuery,
  useRedistributionHistoryQuery,
  useRedistributionContributionsQuery,
  useCreateRedistributionMutation,
  useUpdateRedistributionMutation,
  useDeleteRedistributionMutation,
  useGetAgreementQuery,
  useLazyGetAgreementQuery,
  useUpdateAgreementMutation,
  useResetAgreementsMutation,
} = redistributionApi;
